import { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { Alert } from "../../../components/alert/Alert";
import { Button, InputSelect } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useForm, useWatch } from "react-hook-form";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { usePriceListGetStats } from "../../../hooks/api/v2/priceList/usePriceListGetStats";
import { usePriceListDeleteAndReassign } from "../../../hooks/api/v2/priceList/usePriceListDeleteAndReassign";
import { usePriceListList } from "../../../hooks/api/v2/priceList/usePriceListList";
import {
  PriceListGetStatsResponseType,
  PriceListListResponseType,
} from "@sellar-io/api-v2/types";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { twMerge } from "tailwind-merge";
import { getDiscountDescription } from "../../customer/businessConnection/BusinessConnectionConvertDiscountsDialog.shared";
import { SpinnerLegacy } from "../../../components";
import { AdminOnly } from "../../../components/authFilter/AdminOnly";
import { usePriceListSetDefault } from "../../../hooks/api/v2/priceList/usePriceListSetDefault";

type PriceListDeleteDialogProps = {
  priceListId: number;
  priceListName: string;
  isDefault: boolean;
  active: boolean;
  setActive: (active: boolean) => void;
  openNewPriceList: () => void;
};

export const PriceListDeleteDialog = ({
  priceListId,
  priceListName,
  isDefault,
  active,
  setActive,
  openNewPriceList,
}: PriceListDeleteDialogProps) => {
  const priceListStatsQuery = usePriceListGetStats(priceListId, {
    enabled: Boolean(priceListId),
  });

  const [showCouldNotDeletePage, setShowCouldNotDeletePage] = useState(false);
  const [newPriceListName, setNewPriceListName] = useState("");

  const customersDirectlyAssignedCount =
    priceListStatsQuery.data?.customersDirectlyAssignedCount ?? 0;
  const customerZonesAssigned =
    priceListStatsQuery.data?.customerZonesAssigned ?? [];
  const derivedPriceListCount =
    priceListStatsQuery.data?.derivedPriceListCount ?? 0;

  const hasEntitiesToReassign =
    customersDirectlyAssignedCount > 0 || customerZonesAssigned.length > 0;

  const buildDescription = () => {
    if (showCouldNotDeletePage)
      return `Some customers assigned to '${priceListName}' have customer-level discounts to address before it can be deleted.`;
    if (derivedPriceListCount > 0)
      return "You cannot delete a base price list that has derived price lists";
    if (hasEntitiesToReassign)
      return "To delete this price list, you will need to re-assign a different price list to the individual customers and/or customer zones that currently have it assigned";
    return `Are you sure you want to delete ${priceListName} price list`;
  };

  const description = buildDescription();

  return (
    <>
      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setActive(false)}
          title={
            showCouldNotDeletePage
              ? `'${priceListName}' could not be deleted`
              : `Delete ${priceListName}?`
          }
          description={description}
        />
        {priceListStatsQuery.isLoading ? (
          <DialogContent>
            <SpinnerLegacy />
          </DialogContent>
        ) : (
          <DialogContent>
            {active && !showCouldNotDeletePage && (
              <Content
                priceListId={priceListId}
                isDefault={isDefault}
                setActive={setActive}
                hasEntitiesToReassign={hasEntitiesToReassign}
                customersDirectlyAssignedCount={customersDirectlyAssignedCount}
                customerZonesAssigned={customerZonesAssigned}
                derivedPriceListCount={derivedPriceListCount}
                setShowCouldNotDeletePage={setShowCouldNotDeletePage}
                setNewPriceListName={setNewPriceListName}
              />
            )}
            {active && showCouldNotDeletePage && (
              <CouldNotDeleteContent
                priceListId={priceListId}
                priceListName={priceListName}
                newPriceListName={newPriceListName}
                setActive={setActive}
                openNewPriceList={openNewPriceList}
              />
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

const Content = ({
  priceListId,
  isDefault,
  setActive,
  hasEntitiesToReassign,
  customersDirectlyAssignedCount,
  customerZonesAssigned,
  derivedPriceListCount,
  setShowCouldNotDeletePage,
  setNewPriceListName,
}: {
  priceListId: number;
  isDefault: boolean;
  setActive: (active: boolean) => void;
  hasEntitiesToReassign: boolean;
  customersDirectlyAssignedCount: number;
  customerZonesAssigned: {
    id: number;
    name: string | null;
    customersAssignedCount: number;
  }[];
  derivedPriceListCount: number;
  setShowCouldNotDeletePage: (_show: boolean) => void;
  setNewPriceListName: (_name: string) => void;
}) => {
  const navigate = useNavigate();
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState("");
  const [replacementDefaultId, setReplacementDefaultId] = useState<
    number | null
  >(null);
  const priceListDeleteAndReassignMutation =
    usePriceListDeleteAndReassign(priceListId);
  const setDefaultMutation = usePriceListSetDefault();

  const priceListListQuery = usePriceListList(
    {},
    {
      enabled: Boolean(priceListId),
    },
  );

  const priceListList = priceListListQuery.data?.pages[0] ?? [];

  const handleDeleteAndReassign = (
    data: PriceListDeleteAndReassignFormValues,
  ) => {
    const newPriceList = priceListList.find(
      (priceList) =>
        priceList.id === data.newPriceListIdForDirectlyAssignedCustomers,
    );
    const deleteAndReassign = () =>
      priceListDeleteAndReassignMutation.mutate(
        {
          customerZones: data.customerZones.map((customerZone) => ({
            id: customerZone.customerZoneId,
            newPriceListId: customerZone.newPriceListId,
          })),
          directlyAssignedCustomers:
            data.newPriceListIdForDirectlyAssignedCustomers
              ? {
                  oldPriceListId: priceListId,
                  newPriceListId:
                    data.newPriceListIdForDirectlyAssignedCustomers,
                }
              : null,
          transferDiscountable: data.transferDiscountable,
        },
        {
          onSuccess: (response) => {
            if (response.connectionsWithDiscountsAttached) {
              setNewPriceListName(newPriceList?.name ?? "");
              setShowCouldNotDeletePage(true);
            } else {
              setActive(false);
              navigate(ROUTES.PRICE_LISTS);
            }
          },
          onError: (error) => {
            const message =
              apiV2AssertError(error)?.response?.data.message ?? "";
            setSubmissionErrorMessage(message);
          },
        },
      );

    if (isDefault) {
      if (!replacementDefaultId) return;

      setDefaultMutation.mutate(
        { id: replacementDefaultId },
        {
          onSuccess: deleteAndReassign,
          onError: (error) => {
            const message =
              apiV2AssertError(error)?.response?.data.message ?? "";
            setSubmissionErrorMessage(message);
          },
        },
      );
      return;
    }

    deleteAndReassign();
  };

  const isSubmitting =
    priceListDeleteAndReassignMutation.isPending ||
    setDefaultMutation.isPending;
  const filteredPriceListList = priceListList.filter(
    (priceList) =>
      priceList.id !== priceListId && priceList.derivationType !== "base",
  );
  const replacementDefaultMissing = isDefault && !replacementDefaultId;

  return (
    <div className="flex flex-col gap-4">
      {isDefault && (
        <div className="flex flex-col gap-2">
          <InputLabel>Select a new default price list</InputLabel>
          <InputContainer>
            <InputSelect
              value={replacementDefaultId ?? ""}
              onChange={(event) =>
                setReplacementDefaultId(
                  event.target.value ? Number(event.target.value) : null,
                )
              }
            >
              <option value="">Select a price list…</option>
              {filteredPriceListList.map((priceList) => (
                <option key={priceList.id} value={priceList.id}>
                  {priceList.name}
                </option>
              ))}
            </InputSelect>
          </InputContainer>
        </div>
      )}
      {hasEntitiesToReassign ? (
        <PriceListDeleteAndReassignForm
          customerZonesAssigned={customerZonesAssigned}
          customersDirectlyAssignedCount={customersDirectlyAssignedCount}
          priceListList={priceListList}
          currentPriceListId={priceListId}
          isSubmitting={isSubmitting}
          replacementDefaultMissing={replacementDefaultMissing}
          errorMessage={submissionErrorMessage}
          onSubmit={handleDeleteAndReassign}
        />
      ) : (
        !derivedPriceListCount && (
          <div className="flex justify-end">
            <Button
              color="red"
              disabled={isSubmitting || replacementDefaultMissing}
              loading={isSubmitting}
              onClick={() => {
                handleDeleteAndReassign({
                  customerZones: [],
                  newPriceListIdForDirectlyAssignedCustomers: null,
                  transferDiscountable: false,
                });
              }}
            >
              Delete
            </Button>
          </div>
        )
      )}
    </div>
  );
};

type PriceListDeleteAndReassignFormProps = {
  priceListList: PriceListListResponseType;
  currentPriceListId: number;
  customersDirectlyAssignedCount: number;
  customerZonesAssigned: PriceListGetStatsResponseType["customerZonesAssigned"];
  isSubmitting: boolean;
  replacementDefaultMissing: boolean;
  errorMessage: string;
  onSubmit: (_data: PriceListDeleteAndReassignFormValues) => void;
};

type PriceListDeleteAndReassignFormValues = {
  customerZones: Array<{
    customerZoneId: number;
    newPriceListId: number;
  }>;
  newPriceListIdForDirectlyAssignedCustomers: number | null;
  transferDiscountable: boolean;
};

const PriceListDeleteAndReassignForm = ({
  priceListList,
  currentPriceListId,
  customersDirectlyAssignedCount,
  customerZonesAssigned,
  isSubmitting,
  replacementDefaultMissing,
  errorMessage,
  onSubmit,
}: PriceListDeleteAndReassignFormProps) => {
  const form = useForm<PriceListDeleteAndReassignFormValues>({
    defaultValues: {
      customerZones: [],
      transferDiscountable: false,
    },
  });

  const filteredPriceListList = priceListList.filter(
    (priceList) =>
      priceList.id !== currentPriceListId &&
      priceList.derivationType !== "base",
  );

  const customerZones = useWatch({
    control: form.control,
    name: "customerZones",
  });
  const newPriceListIdForDirectlyAssignedCustomers = useWatch({
    control: form.control,
    name: "newPriceListIdForDirectlyAssignedCustomers",
  });

  const noValues =
    (customerZonesAssigned.length > 0 && !customerZones[0]?.newPriceListId) ||
    !newPriceListIdForDirectlyAssignedCustomers;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <div className="flex flex-col gap-4">
        {customerZonesAssigned.map((customerZoneAssigned, idx) => {
          const customerZoneName =
            customerZoneAssigned.name ??
            `Customer Zone - ${customerZoneAssigned.id}`;
          return (
            <div key={customerZoneAssigned.id} className="flex flex-col gap-2">
              <InputLabel>{`Select price list for your '${customerZoneName}' customer zone`}</InputLabel>
              <InputContainer>
                <InputSelect
                  {...form.register(`customerZones.${idx}`, {
                    required: true,
                    setValueAs: (value) => ({
                      customerZoneId: customerZoneAssigned.id,
                      newPriceListId: Number(value),
                    }),
                  })}
                >
                  <option value="">Select a price list…</option>
                  {filteredPriceListList.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name}
                    </option>
                  ))}
                </InputSelect>
              </InputContainer>
            </div>
          );
        })}
        <div className="flex flex-col gap-2">
          <InputLabel>{`Select price list for ${customersDirectlyAssignedCount} individually assigned customers`}</InputLabel>
          <InputContainer>
            <InputSelect
              {...form.register("newPriceListIdForDirectlyAssignedCustomers", {
                required: true,
                setValueAs: (value) => Number(value),
              })}
            >
              <option value="">Select a price list…</option>
              {filteredPriceListList.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </InputSelect>
          </InputContainer>
        </div>

        {/* Admin-only discountable transfer option */}
        <AdminOnly>
          <div className="flex flex-col gap-2">
            <InputLabel className="text-sm font-medium text-zinc-700">
              Pricing Migration Options
            </InputLabel>
            <div className="flex items-center gap-2 rounded border border-zinc-300 bg-zinc-50 p-3">
              <input
                type="checkbox"
                id="transferDiscountable"
                {...form.register("transferDiscountable")}
                className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor="transferDiscountable"
                className="cursor-pointer text-sm text-zinc-600"
              >
                Transfer discountable field values from old price tier to new
                price tier
                <span className="mt-1 block text-xs text-zinc-500">
                  This will copy the discountable settings for each product from
                  the price tier being deleted to the new price tier. Use this
                  for pricing migrations.
                </span>
              </label>
            </div>
          </div>
        </AdminOnly>
      </div>
      <div className="flex justify-end">
        <Button
          disabled={isSubmitting || noValues || replacementDefaultMissing}
          loading={isSubmitting}
          type="submit"
        >
          Delete and Assign
        </Button>
      </div>
    </form>
  );
};

const CouldNotDeleteContent = ({
  priceListId,
  priceListName,
  newPriceListName,
  setActive,
  openNewPriceList,
}: {
  priceListId: number;
  priceListName: string;
  newPriceListName: string;
  setActive: (_value: boolean) => void;
  openNewPriceList: () => void;
}) => {
  const navigate = useNavigate();

  const supplierRelationshipListQuery = useSupplierRelationshipList({
    query: [
      { type: "hasCustomerLevelDiscounts", modifier: "EQUALS", value: true },
      { type: "priceTierId", modifier: "EQUALS", value: priceListId },
    ],
    limit: 5,
  });

  const connectionList = supplierRelationshipListQuery.data?.pages.flat() ?? [];
  return (
    <>
      <DialogContent className="flex flex-col gap-4">
        <Alert
          type="success"
          title={`Customers without customer-level discounts have been successfully
          assigned '${newPriceListName}'`}
          message=""
        />

        <div className="rounded-lg bg-zinc-100 p-4 text-sm">
          The customers below will remain on{" "}
          <span className="font-semibold">'{priceListName}'</span> as they have
          customer-level discounts assigned. Your next steps could be:
          <ul className="ml-4 list-disc">
            <li>
              Create a new derived price list to replace their discounts with,
              or
            </li>
            <li>
              Go to your Customers' pages to convert their discounts into a new
              derived price list
            </li>
          </ul>
        </div>

        <InfiniteList query={supplierRelationshipListQuery}>
          <div className="grid grid-cols-2 rounded-lg border border-zinc-300 text-sm">
            <span className="rounded-tl-lg border-r border-b border-zinc-300 bg-zinc-50 px-3 py-2 font-semibold">
              Customer
            </span>
            <span className="rounded-tr-lg border-b border-zinc-300 bg-zinc-50 px-3 py-2 font-semibold">
              Customer-level discounts
            </span>

            {connectionList.map((connection, idx) => (
              <Fragment key={connection.id}>
                <span
                  className={twMerge(
                    "border-r border-zinc-300 px-3 py-2",
                    idx !== connectionList.length - 1 && "border-b",
                  )}
                >
                  {connection.Buyer.Business.name}
                </span>
                <span
                  className={twMerge(
                    "border-zinc-300 px-3 py-2",
                    idx !== connectionList.length - 1 && "border-b",
                  )}
                >
                  {getDiscountDescription(connection)}
                </span>
              </Fragment>
            ))}
          </div>
        </InfiniteList>
      </DialogContent>
      <DialogCTA>
        <Button
          color="secondary"
          onClick={() => {
            setActive(false);
            openNewPriceList();
          }}
        >
          Create a new price list
        </Button>
        <Button
          onClick={() => {
            setActive(false);
            navigate(ROUTES.CUSTOMERS);
          }}
        >
          Go to my customers
        </Button>
      </DialogCTA>
    </>
  );
};
